import React, { useState } from "react";
import StatsForm from "./statsForm";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  Tabs,
  Tab,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Chip,
  Button,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BarChartIcon from "@mui/icons-material/BarChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import EqualizerIcon from "@mui/icons-material/Equalizer";

// Note: In a real application, you would use a charting library like Chart.js, Recharts, or D3.js
// For this prototype, we'll create placeholder components to simulate the charts

// Placeholder for radar chart
const RadarChartPlaceholder = ({ data, maxValue = 100 }) => {
  const theme = useTheme();
  const size = 250;
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size * 0.4;

  // Calculate positions for each data point
  const points = data.map((item, index) => {
    const angle = (Math.PI * 2 * index) / data.length - Math.PI / 2;
    const value = Math.min(item.value, maxValue) / maxValue;
    const x = centerX + radius * value * Math.cos(angle);
    const y = centerY + radius * value * Math.sin(angle);
    return { x, y, ...item };
  });

  // Create path for the radar shape
  const pathData =
    points
      .map((point, index) => {
        return (index === 0 ? "M " : "L ") + point.x + " " + point.y;
      })
      .join(" ") + " Z";

  return (
    <Box
      sx={{ width: size, height: size, margin: "0 auto", position: "relative" }}
    >
      <svg width={size} height={size}>
        {/* Background circles */}
        {[0.2, 0.4, 0.6, 0.8, 1].map((level) => (
          <circle
            key={level}
            cx={centerX}
            cy={centerY}
            r={radius * level}
            fill="none"
            stroke={theme.palette.divider}
            strokeWidth="1"
          />
        ))}

        {/* Axis lines */}
        {data.map((item, index) => {
          const angle = (Math.PI * 2 * index) / data.length - Math.PI / 2;
          const x = centerX + radius * Math.cos(angle);
          const y = centerY + radius * Math.sin(angle);
          return (
            <line
              key={index}
              x1={centerX}
              y1={centerY}
              x2={x}
              y2={y}
              stroke={theme.palette.divider}
              strokeWidth="1"
            />
          );
        })}

        {/* Data polygon */}
        <path
          d={pathData}
          fill={theme.palette.primary.main}
          fillOpacity="0.2"
          stroke={theme.palette.primary.main}
          strokeWidth="2"
        />

        {/* Data points */}
        {points.map((point, index) => (
          <circle
            key={index}
            cx={point.x}
            cy={point.y}
            r={4}
            fill={theme.palette.primary.main}
          />
        ))}
      </svg>

      {/* Labels */}
      {points.map((point, index) => {
        const angle = (Math.PI * 2 * index) / data.length - Math.PI / 2;
        const labelRadius = radius + 20;
        const x = centerX + labelRadius * Math.cos(angle);
        const y = centerY + labelRadius * Math.sin(angle);

        return (
          <Typography
            key={index}
            variant="caption"
            sx={{
              position: "absolute",
              left: x,
              top: y,
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              fontWeight: "medium",
            }}
          >
            {point.label}
          </Typography>
        );
      })}
    </Box>
  );
};

// Placeholder for bar chart
const BarChartPlaceholder = ({ data, maxValue = 100 }) => {
  const theme = useTheme();
  const barHeight = 30;
  const width = "100%";

  return (
    <Box sx={{ width }}>
      {data.map((item, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography variant="body2">{item.label}</Typography>
            <Typography variant="body2" fontWeight="medium">
              {item.value}
              {item.unit ? ` ${item.unit}` : ""}
            </Typography>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: barHeight,
              bgcolor: "background.default",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${(item.value / maxValue) * 100}%`,
                bgcolor: item.color || theme.palette.primary.main,
                borderRadius: 1,
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// Placeholder for line chart
const LineChartPlaceholder = ({ data, height = 200 }) => {
  const theme = useTheme();
  const width = "100%";

  // Find min and max values
  const allValues = data.flatMap((series) =>
    series.data.map((point) => point.value)
  );
  const maxValue = Math.max(...allValues);

  // Normalize points to fit in the chart area
  const normalizedData = data.map((series) => ({
    ...series,
    normalizedPoints: series.data.map((point, index) => {
      const x = (index / (series.data.length - 1)) * 100;
      const y = 100 - (point.value / maxValue) * 100;
      return { x, y, ...point };
    }),
  }));

  // Create SVG path for each series
  const createPath = (points) => {
    return points
      .map((point, index) => {
        return (index === 0 ? "M " : "L ") + `${point.x}% ${point.y}%`;
      })
      .join(" ");
  };

  return (
    <Box sx={{ width, height, position: "relative" }}>
      <svg width="100%" height="100%" style={{ overflow: "visible" }}>
        {/* X and Y axis */}
        <line
          x1="0%"
          y1="100%"
          x2="100%"
          y2="100%"
          stroke={theme.palette.divider}
          strokeWidth="1"
        />
        <line
          x1="0%"
          y1="0%"
          x2="0%"
          y2="100%"
          stroke={theme.palette.divider}
          strokeWidth="1"
        />

        {/* Horizontal grid lines */}
        {[0, 25, 50, 75, 100].map((y) => (
          <line
            key={y}
            x1="0%"
            y1={`${100 - y}%`}
            x2="100%"
            y2={`${100 - y}%`}
            stroke={theme.palette.divider}
            strokeWidth="1"
            strokeDasharray="4"
          />
        ))}

        {/* Data lines */}
        {normalizedData.map((series, seriesIndex) => (
          <React.Fragment key={seriesIndex}>
            <path
              d={createPath(series.normalizedPoints)}
              fill="none"
              stroke={series.color || theme.palette.primary.main}
              strokeWidth="2"
            />

            {/* Data points */}
            {series.normalizedPoints.map((point, pointIndex) => (
              <circle
                key={pointIndex}
                cx={`${point.x}%`}
                cy={`${point.y}%`}
                r={4}
                fill={series.color || theme.palette.primary.main}
              />
            ))}
          </React.Fragment>
        ))}
      </svg>

      {/* X-axis labels */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        {normalizedData[0]?.data.map((point, index) => (
          <Typography
            key={index}
            variant="caption"
            sx={{ transform: "translateX(-50%)" }}
          >
            {point.label}
          </Typography>
        ))}
      </Box>

      {/* Legend */}
      <Box sx={{ display: "flex", gap: 2, mt: 2, justifyContent: "center" }}>
        {normalizedData.map((series, index) => (
          <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: series.color || theme.palette.primary.main,
                mr: 0.5,
              }}
            />
            <Typography variant="caption">{series.name}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`performance-tabpanel-${index}`}
      aria-labelledby={`performance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const PerformanceStats = ({ athlete, onStatsUpdate }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [chartType, setChartType] = useState("radar");
  const [timeRange, setTimeRange] = useState("season");
  const [showStatsForm, setShowStatsForm] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChartTypeChange = (event, newValue) => {
    if (newValue !== null) {
      setChartType(newValue);
    }
  };

  const handleTimeRangeChange = (event, newValue) => {
    if (newValue !== null) {
      setTimeRange(newValue);
    }
  };

  // Handle stats form submission
  const handleStatsSave = async (statsData) => {
    try {
      if (onStatsUpdate) {
        await onStatsUpdate(statsData);
      }
      setShowStatsForm(false);
    } catch (error) {
      console.error("Error updating stats:", error);
    }
  };

  // Handle canceling stats form
  const handleStatsCancel = () => {
    setShowStatsForm(false);
  };

  // Generate radar chart data based on athlete position
  const generateRadarData = () => {
    // Get stats with safe defaults
    const stats = athlete.stats || {};
    const pffScore = stats.pffScore || 0;
    const ppiScore = stats.ppiScore || 0;
    const paiScore = stats.paiScore || 0;
    const gamesPlayed = stats.gamesPlayed || 0;
    const gamesStarted = stats.gamesStarted || 0;

    // Different metrics for different positions
    if (athlete.position === "QB" || athlete.position === "Quarterback") {
      const passing = stats.passing || {};
      return [
        { label: "PFF Score", value: Math.min(pffScore, 100) },
        { label: "PPI Score", value: Math.min((ppiScore || 0) * 20, 100) }, // Scale to 0-100
        {
          label: "Completion %",
          value: passing.attempts
            ? Math.min((passing.completions / passing.attempts) * 100, 100)
            : 0,
        },
        {
          label: "YPA",
          value: Math.min((passing.yardsPerAttempt || 0) * 10, 100),
        }, // Scale to 0-100
        {
          label: "TD/INT Ratio",
          value: passing.interceptions
            ? Math.min((passing.touchdowns / passing.interceptions) * 20, 100)
            : (passing.touchdowns || 0) * 20,
        },
        {
          label: "Games Started",
          value: Math.min((gamesStarted / Math.max(gamesPlayed, 1)) * 100, 100),
        },
      ];
    } else if (
      athlete.position === "RB" ||
      athlete.position === "Running Back"
    ) {
      const rushing = stats.rushing || {};
      const receiving = stats.receiving || {};
      return [
        { label: "PFF Score", value: Math.min(pffScore, 100) },
        { label: "PPI Score", value: Math.min((ppiScore || 0) * 20, 100) },
        {
          label: "YPC",
          value: Math.min((rushing.yardsPerCarry || 0) * 20, 100),
        }, // Scale to 0-100
        {
          label: "Rush TDs",
          value: Math.min((rushing.touchdowns || 0) * 10, 100),
        },
        {
          label: "Receptions",
          value: Math.min((receiving.receptions || 0) * 5, 100),
        },
        {
          label: "Games Started",
          value: Math.min((gamesStarted / Math.max(gamesPlayed, 1)) * 100, 100),
        },
      ];
    } else {
      // Default metrics for other positions
      return [
        { label: "PFF Score", value: Math.min(pffScore, 100) },
        { label: "PPI Score", value: Math.min((ppiScore || 0) * 20, 100) },
        { label: "PAI Score", value: Math.min((paiScore || 0) * 20, 100) },
        { label: "Games Played", value: Math.min((gamesPlayed || 0) * 5, 100) },
        {
          label: "Games Started",
          value: Math.min((gamesStarted || 0) * 5, 100),
        },
        {
          label: "Start %",
          value: Math.min((gamesStarted / Math.max(gamesPlayed, 1)) * 100, 100),
        },
      ];
    }
  };

  // Generate bar chart data
  const generateBarData = () => {
    const stats = athlete.stats || {};

    // Different metrics for different positions
    if (athlete.position === "QB" || athlete.position === "Quarterback") {
      const passing = stats.passing || {};
      return [
        {
          label: "Completions",
          value: passing.completions || 0,
          unit: "",
          color: theme.palette.primary.main,
        },
        {
          label: "Passing Yards",
          value: passing.yards || 0,
          unit: "yds",
          color: theme.palette.secondary.main,
        },
        {
          label: "Pass TDs",
          value: passing.touchdowns || 0,
          color: theme.palette.success.main,
        },
        {
          label: "Interceptions",
          value: passing.interceptions || 0,
          color: theme.palette.error.main,
        },
        {
          label: "YPA",
          value: passing.yardsPerAttempt || 0,
          unit: "avg",
          color: theme.palette.info.main,
        },
      ].filter((item) => item.value !== null && item.value !== undefined);
    } else if (
      athlete.position === "RB" ||
      athlete.position === "Running Back"
    ) {
      const rushing = stats.rushing || {};
      const receiving = stats.receiving || {};
      return [
        {
          label: "Rushing Yards",
          value: rushing.yards || 0,
          unit: "yds",
          color: theme.palette.primary.main,
        },
        {
          label: "Yards Per Carry",
          value: rushing.yardsPerCarry || 0,
          unit: "avg",
          color: theme.palette.secondary.main,
        },
        {
          label: "Rushing TDs",
          value: rushing.touchdowns || 0,
          color: theme.palette.success.main,
        },
        {
          label: "Receptions",
          value: receiving.receptions || 0,
          color: theme.palette.info.main,
        },
        {
          label: "Receiving Yards",
          value: receiving.yards || 0,
          unit: "yds",
          color: theme.palette.warning.main,
        },
      ].filter((item) => item.value !== null && item.value !== undefined);
    } else {
      // Default stats for other positions
      return [
        {
          label: "Games Played",
          value: stats.gamesPlayed || 0,
          color: theme.palette.primary.main,
        },
        {
          label: "Games Started",
          value: stats.gamesStarted || 0,
          color: theme.palette.secondary.main,
        },
        {
          label: "Total Snaps",
          value: stats.totalSnaps || 0,
          color: theme.palette.success.main,
        },
        {
          label: "PFF Score",
          value: stats.pffScore || 0,
          color: theme.palette.info.main,
        },
        {
          label: "PPI Score",
          value: stats.ppiScore || 0,
          color: theme.palette.warning.main,
        },
      ].filter((item) => item.value !== null && item.value !== undefined);
    }
  };

  // Generate line chart data
  const generateLineData = () => {
    // Since we don't have game-by-game data, create mock progression based on available stats
    const stats = athlete.stats || {};
    const gamesPlayed = stats.gamesPlayed || 10;

    // Create mock game progression
    const gameProgression = [];
    for (let i = 1; i <= Math.min(gamesPlayed, 12); i++) {
      gameProgression.push({
        label: `G${i}`,
        value: Math.random() * 100 + 50, // Mock progression
      });
    }

    return [
      {
        name: "Performance",
        color: theme.palette.primary.main,
        data: gameProgression,
      },
    ];
  };

  // Generate comparison data
  const generateComparisonData = () => {
    const stats = athlete.stats || {};

    return {
      athlete: {
        gamesPlayed: stats.gamesPlayed || 0,
        gamesStarted: stats.gamesStarted || 0,
        pffScore: stats.pffScore || 0,
        ppiScore: stats.ppiScore || 0,
        paiScore: stats.paiScore || 0,
      },
      averages: {
        gamesPlayed: 15,
        gamesStarted: 8,
        pffScore: 75,
        ppiScore: 2.5,
        paiScore: 2.0,
      },
      topProspect: {
        gamesPlayed: 20,
        gamesStarted: 18,
        pffScore: 95,
        ppiScore: 5.0,
        paiScore: 4.5,
      },
    };
  };

  // Check if athlete has any stats
  const hasStats = athlete.stats && Object.keys(athlete.stats).length > 0;

  // If no stats available, show the stats initialization form
  if (!hasStats) {
    return (
      <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}
        >
          <AssessmentIcon sx={{ mr: 1 }} color="primary" />
          Performance Analysis
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {showStatsForm ? (
          <StatsForm
            athlete={athlete}
            onStatsSave={handleStatsSave}
            onCancel={handleStatsCancel}
          />
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <SportsSoccerIcon
              sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Performance Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Performance statistics will appear here once data is imported or
              manually entered.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AssessmentIcon />}
              onClick={() => setShowStatsForm(true)}
              sx={{ mt: 2 }}
            >
              Initialize Stats
            </Button>
          </Box>
        )}
      </Paper>
    );
  }

  // Radar chart data
  const radarData = generateRadarData();

  // Bar chart data
  const barData = generateBarData();

  // Line chart data
  const lineData = generateLineData();

  // Comparison data
  const comparisonData = generateComparisonData();

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}
        >
          <AssessmentIcon sx={{ mr: 1 }} color="primary" />
          Performance Analysis
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AssessmentIcon />}
          onClick={() => setShowStatsForm(true)}
        >
          Edit Stats
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {/* Show stats form if editing, otherwise show stats */}
      {showStatsForm ? (
        <StatsForm
          athlete={athlete}
          onStatsSave={handleStatsSave}
          onCancel={handleStatsCancel}
          initialStats={athlete.stats}
        />
      ) : (
        <>
          {/* Add a basic stats summary table */}
          <Card sx={{ mb: 3, bgcolor: "background.default" }}>
            <CardContent>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                Quick Stats Summary
              </Typography>
              <Grid container spacing={2}>
                {athlete.stats.gamesPlayed && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Games Played
                    </Typography>
                    <Typography variant="h6">
                      {athlete.stats.gamesPlayed}
                    </Typography>
                  </Grid>
                )}
                {athlete.stats.gamesStarted && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Games Started
                    </Typography>
                    <Typography variant="h6">
                      {athlete.stats.gamesStarted}
                    </Typography>
                  </Grid>
                )}
                {athlete.stats.pffScore && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      PFF Score
                    </Typography>
                    <Typography variant="h6">
                      {athlete.stats.pffScore}
                    </Typography>
                  </Grid>
                )}
                {athlete.stats.ppiScore && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      PPI Score
                    </Typography>
                    <Typography variant="h6">
                      {athlete.stats.ppiScore}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Chart controls */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <ToggleButtonGroup
              value={chartType}
              exclusive
              onChange={handleChartTypeChange}
              aria-label="chart type"
              size="small"
            >
              <ToggleButton value="radar" aria-label="radar chart">
                <Tooltip title="Skills Radar">
                  <EqualizerIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="bar" aria-label="bar chart">
                <Tooltip title="Stats Breakdown">
                  <BarChartIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="line" aria-label="line chart">
                <Tooltip title="Game-by-Game">
                  <TimelineIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="compare" aria-label="comparison">
                <Tooltip title="Comparisons">
                  <CompareArrowsIcon />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup
              value={timeRange}
              exclusive
              onChange={handleTimeRangeChange}
              aria-label="time range"
              size="small"
            >
              <ToggleButton value="season" aria-label="current season">
                Current Season
              </ToggleButton>
              <ToggleButton value="career" aria-label="career">
                Career
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Charts */}
          <Box sx={{ minHeight: 350 }}>
            {chartType === "radar" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: "medium" }}
                >
                  Skills Assessment
                  <Tooltip title="Based on coach evaluations and performance metrics">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <RadarChartPlaceholder data={radarData} maxValue={100} />
              </Box>
            )}

            {chartType === "bar" && (
              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: "medium" }}
                >
                  {timeRange === "season" ? "Current Season" : "Career"}{" "}
                  Statistics
                </Typography>
                <BarChartPlaceholder
                  data={barData}
                  maxValue={
                    Math.max(...barData.map((item) => item.value)) * 1.2
                  }
                />
              </Box>
            )}

            {chartType === "line" && (
              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: "medium" }}
                >
                  Game-by-Game Performance
                </Typography>
                <LineChartPlaceholder data={lineData} height={300} />
              </Box>
            )}

            {chartType === "compare" && (
              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: "medium" }}
                >
                  Comparison with Averages and Top Prospects
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "background.default" }}>
                        <TableCell>
                          <strong>Statistic</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{athlete.firstName}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>League Avg</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Top Prospect</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>% of Top</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.keys(comparisonData.athlete)
                        .filter(
                          (key) =>
                            typeof comparisonData.athlete[key] === "number"
                        )
                        .map((key) => {
                          const athleteValue = comparisonData.athlete[key];
                          const avgValue = comparisonData.averages[key];
                          const topValue = comparisonData.topProspect[key];
                          const percentOfTop = topValue
                            ? Math.round((athleteValue / topValue) * 100)
                            : 0;

                          // Format the key for display
                          const formattedKey = key
                            .replace(/([A-Z])/g, " $1") // Add space before capital letters
                            .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter

                          return (
                            <TableRow key={key}>
                              <TableCell>{formattedKey}</TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: "medium",
                                  color:
                                    athleteValue > avgValue
                                      ? theme.palette.success.main
                                      : "inherit",
                                }}
                              >
                                {athleteValue}
                              </TableCell>
                              <TableCell align="right">{avgValue}</TableCell>
                              <TableCell align="right">{topValue}</TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: "medium",
                                  color:
                                    percentOfTop >= 90
                                      ? theme.palette.success.main
                                      : percentOfTop >= 70
                                      ? theme.palette.success.light
                                      : percentOfTop >= 50
                                      ? theme.palette.warning.main
                                      : theme.palette.error.light,
                                }}
                              >
                                {percentOfTop}%
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Tabs for different performance aspects */}
      <Box sx={{ mt: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="performance tabs"
          variant="fullWidth"
        >
          <Tab
            icon={<SportsSoccerIcon />}
            label="Game Stats"
            id="performance-tab-0"
            aria-controls="performance-tabpanel-0"
          />
          <Tab
            icon={<AssessmentIcon />}
            label="Scouting Report"
            id="performance-tab-1"
            aria-controls="performance-tabpanel-1"
          />
        </Tabs>

        {/* Game Stats Tab */}
        <TabPanel value={tabValue} index={0}>
          {athlete.stats && Object.keys(athlete.stats).length > 0 ? (
            <Grid container spacing={3}>
              {/* Basic Game Statistics */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: "medium" }}
                    >
                      Season Statistics
                    </Typography>
                    <Grid container spacing={2}>
                      {athlete.stats.gamesPlayed && (
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography variant="h4" color="primary">
                              {athlete.stats.gamesPlayed}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Games Played
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {athlete.stats.gamesStarted && (
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography variant="h4" color="secondary">
                              {athlete.stats.gamesStarted}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Games Started
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {athlete.stats.totalSnaps && (
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography variant="h4" color="success.main">
                              {athlete.stats.totalSnaps}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Snaps
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {athlete.stats.gamesPlayed &&
                        athlete.stats.gamesStarted && (
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography variant="h4" color="info.main">
                                {Math.round(
                                  (athlete.stats.gamesStarted /
                                    athlete.stats.gamesPlayed) *
                                    100
                                )}
                                %
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Start Percentage
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Performance Scores */}
              {(athlete.stats.pffScore ||
                athlete.stats.ppiScore ||
                athlete.stats.paiScore) && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "medium" }}
                      >
                        Performance Scores
                      </Typography>
                      <Grid container spacing={2}>
                        {athlete.stats.pffScore && (
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography variant="h4" color="primary">
                                {athlete.stats.pffScore}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                PFF Score
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {athlete.stats.ppiScore && (
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography variant="h4" color="secondary">
                                {athlete.stats.ppiScore}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                PPI Score
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {athlete.stats.paiScore && (
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography variant="h4" color="success.main">
                                {athlete.stats.paiScore}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                PAI Score
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Position-Specific Stats */}
              {athlete.stats.passing && (
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "medium" }}
                      >
                        Passing Statistics
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableBody>
                            {athlete.stats.passing.completions &&
                              athlete.stats.passing.attempts && (
                                <TableRow>
                                  <TableCell>Completions/Attempts</TableCell>
                                  <TableCell align="right">
                                    {athlete.stats.passing.completions}/
                                    {athlete.stats.passing.attempts}
                                    {athlete.stats.passing.attempts && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ ml: 1 }}
                                      >
                                        (
                                        {Math.round(
                                          (athlete.stats.passing.completions /
                                            athlete.stats.passing.attempts) *
                                            100
                                        )}
                                        %)
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              )}
                            {athlete.stats.passing.yards && (
                              <TableRow>
                                <TableCell>Passing Yards</TableCell>
                                <TableCell align="right">
                                  {athlete.stats.passing.yards}
                                </TableCell>
                              </TableRow>
                            )}
                            {athlete.stats.passing.touchdowns && (
                              <TableRow>
                                <TableCell>Touchdowns</TableCell>
                                <TableCell align="right">
                                  {athlete.stats.passing.touchdowns}
                                </TableCell>
                              </TableRow>
                            )}
                            {athlete.stats.passing.interceptions && (
                              <TableRow>
                                <TableCell>Interceptions</TableCell>
                                <TableCell align="right">
                                  {athlete.stats.passing.interceptions}
                                </TableCell>
                              </TableRow>
                            )}
                            {athlete.stats.passing.yardsPerAttempt && (
                              <TableRow>
                                <TableCell>Yards Per Attempt</TableCell>
                                <TableCell align="right">
                                  {athlete.stats.passing.yardsPerAttempt}
                                </TableCell>
                              </TableRow>
                            )}
                            {athlete.stats.passing.yardsPerGame && (
                              <TableRow>
                                <TableCell>Yards Per Game</TableCell>
                                <TableCell align="right">
                                  {athlete.stats.passing.yardsPerGame}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {athlete.stats.rushing && (
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "medium" }}
                      >
                        Rushing Statistics
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableBody>
                            {athlete.stats.rushing.attempts && (
                              <TableRow>
                                <TableCell>Attempts</TableCell>
                                <TableCell align="right">
                                  {athlete.stats.rushing.attempts}
                                </TableCell>
                              </TableRow>
                            )}
                            {athlete.stats.rushing.yards && (
                              <TableRow>
                                <TableCell>Rushing Yards</TableCell>
                                <TableCell align="right">
                                  {athlete.stats.rushing.yards}
                                </TableCell>
                              </TableRow>
                            )}
                            {athlete.stats.rushing.touchdowns && (
                              <TableRow>
                                <TableCell>Touchdowns</TableCell>
                                <TableCell align="right">
                                  {athlete.stats.rushing.touchdowns}
                                </TableCell>
                              </TableRow>
                            )}
                            {athlete.stats.rushing.yardsPerCarry && (
                              <TableRow>
                                <TableCell>Yards Per Carry</TableCell>
                                <TableCell align="right">
                                  {athlete.stats.rushing.yardsPerCarry}
                                </TableCell>
                              </TableRow>
                            )}
                            {athlete.stats.rushing.yardsPerGame && (
                              <TableRow>
                                <TableCell>Yards Per Game</TableCell>
                                <TableCell align="right">
                                  {athlete.stats.rushing.yardsPerGame}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {athlete.stats.receiving && (
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "medium" }}
                      >
                        Receiving Statistics
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableBody>
                            {athlete.stats.receiving.receptions && (
                              <TableRow>
                                <TableCell>Receptions</TableCell>
                                <TableCell align="right">
                                  {athlete.stats.receiving.receptions}
                                </TableCell>
                              </TableRow>
                            )}
                            {athlete.stats.receiving.yards && (
                              <TableRow>
                                <TableCell>Receiving Yards</TableCell>
                                <TableCell align="right">
                                  {athlete.stats.receiving.yards}
                                </TableCell>
                              </TableRow>
                            )}
                            {athlete.stats.receiving.touchdowns && (
                              <TableRow>
                                <TableCell>Touchdowns</TableCell>
                                <TableCell align="right">
                                  {athlete.stats.receiving.touchdowns}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <SportsSoccerIcon
                sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Game Statistics Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Performance statistics will appear here once data is imported or
                manually entered.
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* Scouting Report Tab */}
        <TabPanel value={tabValue} index={1}>
          {(athlete.notes && athlete.notes.length > 0) ||
          athlete.scoutingReport ? (
            <Grid container spacing={3}>
              {/* Notes Section */}
              {athlete.notes && athlete.notes.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "medium" }}
                      >
                        Notes & Observations
                      </Typography>
                      <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                        {athlete.notes.map((note, index) => (
                          <Card
                            key={note._id || note.id || index}
                            variant="outlined"
                            sx={{ mb: 2, bgcolor: "background.default" }}
                          >
                            <CardContent sx={{ py: 2 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  mb: 1,
                                }}
                              >
                                <Chip
                                  label={note.category || "general"}
                                  size="small"
                                  color={
                                    note.category === "recruiting"
                                      ? "primary"
                                      : note.category === "academic"
                                      ? "secondary"
                                      : note.category === "athletic"
                                      ? "success"
                                      : note.category === "character"
                                      ? "info"
                                      : "default"
                                  }
                                />
                                {note.createdAt && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {new Date(
                                      note.createdAt
                                    ).toLocaleDateString()}
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="body2">
                                {note.text}
                              </Typography>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Legacy Scouting Report (if exists) */}
              {athlete.scoutingReport && (
                <>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ fontWeight: "medium" }}
                        >
                          Strengths
                        </Typography>
                        <Box component="ul" sx={{ pl: 2 }}>
                          {(athlete.scoutingReport.strengths || []).map(
                            (strength, index) => (
                              <Typography
                                component="li"
                                key={index}
                                variant="body2"
                                paragraph
                              >
                                {strength}
                              </Typography>
                            )
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ fontWeight: "medium" }}
                        >
                          Areas for Improvement
                        </Typography>
                        <Box component="ul" sx={{ pl: 2 }}>
                          {(athlete.scoutingReport.weaknesses || []).map(
                            (weakness, index) => (
                              <Typography
                                component="li"
                                key={index}
                                variant="body2"
                                paragraph
                              >
                                {weakness}
                              </Typography>
                            )
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{ fontWeight: "medium" }}
                        >
                          Scout's Summary
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {athlete.scoutingReport.summary ||
                            "No summary available."}
                        </Typography>

                        <Box
                          sx={{ display: "flex", alignItems: "center", mt: 2 }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mr: 1 }}
                          >
                            Overall Grade:
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            sx={{
                              color:
                                (athlete.scoutingReport.grade || 0) >= 9
                                  ? theme.palette.success.main
                                  : (athlete.scoutingReport.grade || 0) >= 7
                                  ? theme.palette.success.light
                                  : (athlete.scoutingReport.grade || 0) >= 5
                                  ? theme.palette.warning.main
                                  : theme.palette.error.light,
                            }}
                          >
                            {athlete.scoutingReport.grade || "N/A"}/10
                          </Typography>
                          {athlete.scoutingReport.lastUpdated && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ ml: 2 }}
                            >
                              Last Updated:{" "}
                              {new Date(
                                athlete.scoutingReport.lastUpdated
                              ).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              )}
            </Grid>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <AssessmentIcon
                sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Scouting Information Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Notes and scouting reports will appear here once evaluations are
                completed.
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Box>
    </Paper>
  );
};

export default PerformanceStats;
